import { throttle } from 'lodash'
import { useCallback, useContext, useEffect, useState } from 'react'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import CreationToolFont from '../../../types/font'
import CreationToolFontSize from '../../../types/font-size'
import { preloadFont } from '../../../utils/preload'
import TextLayer from '../../artboard/layer/text'
import { ArtboardDragPosition } from '../../artboard/types'
import { CreationSourceEditorContext } from '../manager'

type Props = {
    canvasScale?: number
}

const CreationToolsSourceEditorArtboardCaptions = ({ canvasScale = 1 }: Props) => {
    const { source, edits, captions, setCaptions, customFontUrl, crop, forceRedraw, setForceRedraw } =
        useContext(CreationSourceEditorContext)
    const [isFontLoaded, setIsFontLoaded] = useState<boolean>(false)

    useAsyncEffect(async () => {
        if (customFontUrl) {
            try {
                setIsFontLoaded(false)
                await preloadFont(customFontUrl, CreationToolFont.fontFamily(CreationToolFont.Custom))
            } finally {
                setIsFontLoaded(true)
            }
        } else {
            setIsFontLoaded(true)
        }
    }, [customFontUrl, setIsFontLoaded])

    const onMoveCaption = useCallback(
        throttle((index: number, { x, y, rotation }: ArtboardDragPosition) => {
            setCaptions(
                captions.map((c, i) => {
                    if (i === index) {
                        return {
                            ...c,
                            x: Math.round(x * canvasScale),
                            y: Math.round(y * canvasScale),
                            rotation: Math.round(rotation),
                        }
                    }
                    return c
                })
            )
        }, 50),
        [captions, setCaptions]
    )

    useEffect(() => {
        if (forceRedraw) {
            setForceRedraw(false)
        }
    }, [forceRedraw])

    if (forceRedraw) {
        return null
    }

    if (!isFontLoaded) {
        return null
    }

    return (
        <>
            {captions.map((caption, i) => {
                const {
                    text,
                    x,
                    y,
                    rotation,
                    maxWidth = (edits?.crop?.width || source.width) * 0.9,
                    style: { color, strokeColor, font, fontSize },
                } = caption
                return (
                    <TextLayer
                        key={`artboard-caption-layer-${i}`}
                        {...{
                            defaultX: x / canvasScale,
                            defaultY: y / canvasScale,
                            defaultRotation: rotation,
                            maxWidth: maxWidth / canvasScale,
                            fontFamily: CreationToolFont.fontFamily(font),
                            fontSize:
                                CreationToolFontSize.sizeBaseOnHeight(fontSize) *
                                CreationToolFont.scale(font) *
                                crop.height,
                            fontWeight: CreationToolFont.fontWeight(font),
                            text: CreationToolFont.isAllCaps(font) ? text.toUpperCase() : text,
                            resizeable: false,
                            rotateable: true,
                            moveable: true,
                            onMove: (position) => onMoveCaption(i, position),
                            color,
                            strokeColor,
                            caption,
                            index: i,
                        }}
                    />
                )
            })}
        </>
    )
}

export default CreationToolsSourceEditorArtboardCaptions
